import React from 'react';
import { LenderMatchDrillIn } from '../../shared/models';
import { TableCell, TableRow, Collapse } from '@mui/material';
import { CerebroCheckbox, CerebroIcon, CerebroLink, CerebroText, LenderStoreDot } from '../../shared/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field } from 'formik';
import NumberFormat from 'react-number-format';
import { LenderMatchRowActionButton } from './LenderMatchRowActionButton';
import { faCheck, faTimes, faLocationDot, faCommentDollar } from '@fortawesome/free-solid-svg-icons';

interface LenderMatchTableRowProps {
	lender: LenderMatchDrillIn;
	isSubmitting: boolean;
	isChecked: boolean;
	allowSelection: boolean;
	showCapacityColumn: boolean;
	onChange: (response: LenderMatchRowClickResponse) => void;
}

export interface LenderMatchRowClickResponse {
	bankDealProfileGuid: string;
	isChecked: boolean;
}

export const LenderMatchTableRow: React.FC<LenderMatchTableRowProps> = ({
	lender,
	isSubmitting,
	isChecked,
	allowSelection,
	showCapacityColumn,
	onChange,
}) => {
	const [toggleSummary, setToggleSummary] = React.useState<boolean>(false);
	let coverage = lender.coverageAreas.split(', ');
	if (lender.sbaPariPassuLending) {
		coverage.push('SBA Pari Passu Lending');
	}
	const columnCount = showCapacityColumn ? 6 : 5;

	return (
		<>
			<TableRow>
				<TableCell>
					{lender.isPicked ? (
						<CerebroIcon
							padLeft
							sx={theme => ({
								color: theme.palette.success.main,
							})}
							title="Already Added to Deal"
						>
							<FontAwesomeIcon icon={faCheck} size="sm" />
						</CerebroIcon>
					) : lender.isExcluded ? (
						<CerebroIcon
							padLeft
							sx={theme => ({
								color: theme.palette.error.main,
							})}
							title="Excluded Bank of Deal"
						>
							<FontAwesomeIcon icon={faTimes} size="sm" />
						</CerebroIcon>
					) : allowSelection ? (
						<Field
							key={lender.bankDealProfileGuid}
							as={CerebroCheckbox}
							name={lender.bankDealProfileGuid}
							disabled={isSubmitting}
							checked={isChecked}
							onChange={(e: any) => {
								onChange({
									bankDealProfileGuid: e.target.name,
									isChecked: e.target.checked,
								} as LenderMatchRowClickResponse);
							}}
						/>
					) : (
						<></>
					)}
				</TableCell>
				<TableCell>
					<LenderStoreDot score={lender.score} isFlagged={lender.lenderScoreFlag} sx={{ marginRight: 0.5 }} />
					{lender.isInRange && <FontAwesomeIcon icon={faLocationDot} title="Is Local" />} {lender.lenderName}
				</TableCell>
				<TableCell>
					{lender.bankName}
					{lender.affiliateLender && (
						<CerebroText component="p" variant="hint">
							({lender.affiliateLender})
						</CerebroText>
					)}
				</TableCell>
				{showCapacityColumn && (
					<TableCell>
						<NumberFormat
							value={lender.dealSizeAmount}
							displayType="text"
							thousandSeparator={true}
							prefix="$"
						/>
					</TableCell>
				)}
				<TableCell>
					{coverage.map((coverageArea, index) => {
						const [area, finderFee] = coverageArea.split(':');

						const isLastItem = index === coverage.length - 1;
						const hasFinderFeeAgreement = finderFee === 'FF';

						return (
							<CerebroText key={area} variant="small" component="span">
								{hasFinderFeeAgreement && (
									<CerebroIcon
										sx={theme => ({
											color: theme.palette.success.main,
											paddingX: 0.5,
										})}
										title={`${lender.bankName} has finder fee agreement(s) for ${area}`}
									>
										<FontAwesomeIcon icon={faCommentDollar} size="sm" />
									</CerebroIcon>
								)}
								{isLastItem ? area : `${area}, `}
							</CerebroText>
						);
					})}
				</TableCell>
				<TableCell>
					<LenderMatchRowActionButton
						lender={lender}
						summaryActive={toggleSummary}
						toggleSummary={setToggleSummary}
					/>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={columnCount} sx={{ paddingBottom: 0, paddingTop: 0 }}>
					<Collapse in={toggleSummary} timeout="auto" unmountOnExit={false}>
						<CerebroText variant="small" sx={{ marginTop: 1, marginBottom: 1 }}>
							{lender.matchingSummary === '' ? 'No summary available' : lender.matchingSummary}
							<br />
							<CerebroLink
								variant="small"
								sx={{ cursor: 'pointer' }}
								onClick={() => setToggleSummary(false)}
							>
								[Close Summary]
							</CerebroLink>
						</CerebroText>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
};
